import React from 'react';
import { Link } from 'react-router-dom';
import { Calculator, TrendingUp, DollarSign, Shield, PieChart, Target, Building2, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const CalculatorsSection = () => {
  const calculatorTypes = [
    {
      id: 'sip',
      title: 'SIP Calculator',
      description: 'Calculate returns on systematic investments with compound interest',
      icon: TrendingUp,
      category: 'Investment',
      color: 'from-green-500 to-emerald-600'
    },
    {
      id: 'lumpsum',
      title: 'Lumpsum Calculator',
      description: 'Plan one-time investment strategies for maximum returns',
      icon: DollarSign,
      category: 'Investment',
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'emi',
      title: 'EMI Calculator',
      description: 'Calculate loan repayment schedules and interest payments',
      icon: Building2,
      category: 'Loan',
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 'ppf',
      title: 'PPF Calculator',
      description: 'Plan your Public Provident Fund with tax benefits',
      icon: Shield,
      category: 'Tax Saving',
      color: 'from-orange-500 to-orange-600'
    },
    {
      id: 'fd',
      title: 'FD Calculator',
      description: 'Calculate fixed deposit returns and maturity amounts',
      icon: PieChart,
      category: 'Investment',
      color: 'from-red-500 to-red-600'
    },
    {
      id: 'retirement',
      title: 'Retirement Calculator',
      description: 'Plan your retirement corpus and monthly requirements',
      icon: Target,
      category: 'Planning',
      color: 'from-indigo-500 to-indigo-600'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden" id="calculators">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/5 via-transparent to-black/5" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-gray-900 to-gray-700 rounded-2xl mb-6 shadow-lg">
            <Calculator className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
            Free Financial Calculators
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Plan your investments, loans, and financial goals with our comprehensive suite of financial calculators. 
            Get accurate calculations for SIP, EMI, PPF, FD, and more.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {calculatorTypes.map((calculator, index) => {
            const IconComponent = calculator.icon;
            return (
              <Card 
                key={calculator.id} 
                className="group hover:shadow-2xl transition-all duration-500 hover:scale-105 bg-white border-2 hover:border-gray-300 overflow-hidden animate-[fadeInScale_0.6s_ease-out_0.1s_both]"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${calculator.color} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <span className="inline-block bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide mb-2">
                        {calculator.category}
                      </span>
                      <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-gray-700 transition-colors duration-300">
                        {calculator.title}
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {calculator.description}
                  </p>
                  {/* <div className="flex items-center text-sm text-gray-500 group-hover:text-gray-700 transition-colors duration-300">
                    <span>Click to calculate</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                  </div> */}
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        <div className="text-center">
          <Button asChild className="group bg-gradient-to-r from-gray-900 to-gray-700 hover:from-gray-800 hover:to-gray-600 text-white px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
            <Link to="/calculators" className="flex items-center space-x-3">
              <Calculator className="w-6 h-6" />
              <span>Access All 15+ Calculators</span>
            </Link>
          </Button>
          <p className="mt-6 text-gray-600 font-medium">
            <span className="inline-flex items-center space-x-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              <span>Free to use</span>
            </span>
            <span className="mx-4">•</span>
            <span className="inline-flex items-center space-x-2">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              <span>No registration required</span>
            </span>
            <span className="mx-4">•</span>
            <span className="inline-flex items-center space-x-2">
              <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
              <span>Instant calculations</span>
            </span>
          </p>
        </div>
      </div>

      {/* CSS Keyframes for animations */}
      <style jsx>{`
        @keyframes fadeInScale {
          from { 
            opacity: 0; 
            transform: scale(0.9); 
          }
          to { 
            opacity: 1; 
            transform: scale(1); 
          }
        }
      `}</style>
    </section>
  );
};

export default CalculatorsSection;